/**
 * Timing Constants
 * 
 * SOURCE OF TRUTH for all delay, timeout, and timing values.
 * Use these constants instead of hardcoding millisecond values.
 */

/**
 * Common delay values for UI interactions
 */
export const DELAYS = {
  /** Debounce delay for search inputs */
  DEBOUNCE: 300,
  /** Slower debounce for expensive operations */
  DEBOUNCE_SLOW: 500,
  /** Throttle delay for scroll/resize handlers */
  THROTTLE: 150,
  /** Toast notification display duration */
  TOAST_DURATION: 5000,
  /** Short toast duration */
  TOAST_SHORT: 3000,
  /** Long toast duration */
  TOAST_LONG: 8000,
  /** Default toast duration */
  TOAST_DEFAULT: 5000,
  /** Polling interval for background updates */
  REFETCH_INTERVAL: 30000,
  /** Animation transition duration */
  TRANSITION: 200,
  /** Fast animation duration */
  ANIMATION_FAST: 150,
  /** Normal animation duration */
  ANIMATION_NORMAL: 300,
  /** Delay before showing loading states */
  LOADING_THRESHOLD: 500,
  /** One second in ms */
  SECOND: 1000,
} as const;

/**
 * React Query staleTime values
 */
export const STALE_TIMES = {
  /** 30 seconds - for frequently changing data */
  SHORT: 30_000,
  /** 1 minute - default for most queries */
  MEDIUM: 60_000,
  /** 5 minutes - for semi-static data */
  LONG: 300_000,
  /** 1 hour - for rarely changing data */
  VERY_LONG: 3_600_000,
  /** Infinite - for static data (never refetch) */
  INFINITE: Infinity,
} as const;

/**
 * API and operation timeouts
 */
export const TIMEOUTS = {
  /** Standard API request timeout */
  API_REQUEST: 30_000,
  /** File upload timeout */
  FILE_UPLOAD: 120_000,
  /** Background job timeout */
  BACKGROUND_JOB: 300_000,
  /** Real-time subscription reconnect timeout */
  REALTIME_RECONNECT: 5_000,
} as const;

/**
 * Retry configuration
 */
export const RETRY_CONFIG = {
  /** Maximum retry attempts */
  MAX_ATTEMPTS: 3,
  /** Base delay for exponential backoff (ms) */
  BASE_DELAY: 1000,
  /** Maximum delay between retries (ms) */
  MAX_DELAY: 30_000,
} as const;

/**
 * Health status thresholds for agent monitoring
 */
export const HEALTH_THRESHOLDS = {
  /** Minutes of inactivity before showing "idle" status (24 hours) */
  IDLE_MINUTES: 1440,
  /** Minutes of inactivity before showing "down" status (7 days) */
  DOWN_MINUTES: 10080,
  /** Minimum success rate for "healthy" status */
  HEALTHY_SUCCESS_RATE: 95,
  /** Minimum success rate before "down" status */
  CRITICAL_SUCCESS_RATE: 50,
  /** Maximum latency (ms) for "healthy" status */
  HEALTHY_LATENCY_MS: 5000,
  /** Maximum latency (ms) before "degraded" status */
  DEGRADED_LATENCY_MS: 10000,
} as const;
